// Our base component, which all others extend.
import { OutlineElement } from '../../base/outline-element/outline-element';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import componentStyles from './se-grid.css.lit';
import { styleMap } from 'lit/directives/style-map.js';
import { SlotController } from '../../controllers/slot-controller';
import '../se-link/se-link';
import '../se-heading/se-heading';
import {
  AllowedHeadingLevels,
  AllowedHeadingSizes,
} from '../se-heading/config';

/**
 * The Outline  Link component
 * @slot - The default, and only slot for this element.
 */
@customElement('se-grid')
export class SEGrid extends OutlineElement {
  slots = new SlotController(this, false);
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Vertical gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'row-gap',
  })
  rowGap = '1rem';

  /**
   * Horizontal gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'col-gap',
  })
  colGap = '1rem';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'columns',
  })
  columns = '3';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'item-min-width',
  })
  itemMinWidth = '200px';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'align-self',
  })
  alignSelf = '';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'justify-self',
  })
  justifySelf = '';

  /**
   * Whether or not this is a full bleed container.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'full-bleed',
  })
  fullBleed = false;

  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height',
  })
  equalHeight = false;

  @property({ type: String, attribute: 'heading-level' })
  headingLevel: AllowedHeadingLevels = 'h2';

  @property({ type: String, attribute: 'heading-size' })
  headingSize: AllowedHeadingSizes;

  @property({ type: String, reflect: true, attribute: 'grid-title' })
  gridTitle = '';

  @property({ type: String, reflect: true, attribute: 'cta-url-1' })
  ctaUrl1 = '';

  @property({ type: String, reflect: true, attribute: 'cta-text-1' })
  ctaText1 = '';

  @property({ type: String, reflect: true, attribute: 'cta-url-2' })
  ctaUrl2 = '';

  @property({ type: String, reflect: true, attribute: 'cta-text-2' })
  ctaText2 = '';

  @state() hasTitleSlot: boolean;

  firstUpdated(): void {
    this.hasTitleSlot = this.slots.test('title');
  }

  /**
   * Return the container element.
   */
  render(): TemplateResult {
    const gridStyles = {
      '--grid-layout-column-gap': this.colGap,
      '--grid-layout-row-gap': this.rowGap,
      '--grid-column--max-count': this.columns,
      '--grid-item--min-width': this.itemMinWidth,
      '--align-self': this.alignSelf || 'auto',
      '--justify-self': this.justifySelf || 'auto',
    };
    return html` ${this.gridHeaderTemplate()}
      <div
        class="grid-wrapper
      ${this.alignSelf ? 'align-self' : ''}
      ${this.justifySelf ? 'justify-self' : ''}
      ${this.equalHeight ? 'equal-height' : ''}
      ${this.fullBleed ? 'full-bleed' : ''}"
        style=${styleMap(gridStyles)}
      >
        <slot></slot>
      </div>`;
  }

  gridHeaderTemplate(): TemplateResult {
    return this.hasTitleSlot
      ? html`<div class="grid-header">
          <slot name="title"></slot>
          ${this.ctaUrl1 ? this.gridHeaderCtaTemplate() : ''}
        </div>`
      : html` ${this.gridTitle
          ? html`
              <div class="grid-header">
                <se-heading
                  level="${this.headingLevel}"
                  level-size="${ifDefined(this.headingSize)}"
                  >${this.gridTitle}
                </se-heading>
                ${this.ctaUrl1 ? this.gridHeaderCtaTemplate() : ''}
              </div>
            `
          : ''}`;
  }

  gridHeaderCtaTemplate(): TemplateResult {
    return html`
      <div class="grid-cta">
        <se-link
          size="small"
          variant="cta"
          link-href="${this.ctaUrl1}"
          link-text="${this.ctaText1}"
        >
        </se-link>
        ${this.ctaUrl2
          ? html`
              <se-link
                size="small"
                variant="cta"
                link-href="${this.ctaUrl2}"
                link-text="${this.ctaText2}"
              >
              </se-link>
            `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-grid': SEGrid;
  }
}
